import {type PaymentCard, formatCardNumber} from '@yandex-delivery/api/model/payment'
import {useTransition} from '@yandex-delivery/blocks'
import {type ServerError} from '@yandex-delivery/core/utils/network'
import {useController, useFormContext} from '@yandex-delivery/form'
import {type Optional} from '@yandex-delivery/types'

import {i18n} from 'src/shared/config'
import {useOrderAvailability} from 'src/shared/hooks'
import type {Order} from 'src/shared/types'

import {CardErrorCode, fetchCard} from './api'
import {type Props} from './types'
import {getIconSrc} from './utils'

export function useCard({name, getBody: getBodyProp}: Props) {
  const {startOrderAvailabilityTransition} = useOrderAvailability()
  const {control, getFieldValue} = useFormContext<Order>()
  const {field, fieldState} = useController({
    // @ts-expect-error проблемы типизации RHF
    name,
    control,
  })
  const card: Optional<PaymentCard> = field.value
  const title = card ? formatCardNumber(card) : i18n('profile.payment-card', 'Карта')

  function changeHandler(nextCard: PaymentCard) {
    if (card && card.id === nextCard.id) {
      return
    }

    field.onChange(nextCard)
    startOrderAvailabilityTransition()
  }

  const [{isPending, error}] = useTransition<Optional<PaymentCard>, ServerError>(
    () => fetchCard({onSuccess: changeHandler}),
    {key: `fetch/order-form/cards/${name}`},
  )

  return {
    unauthorized: error?.message === CardErrorCode.UNAUTHORIZED || error?.status === 401 ? error.message : undefined,
    errorText: fieldState.invalid
      ? i18n('order.form.error.empty-payment-card', 'Выберите карту для оплаты')
      : undefined,
    isPending,
    title,
    iconSrc: getIconSrc(card?.system),
    card,
    getBody() {
      return getBodyProp(getFieldValue())
    },
    changeHandler,
  } as const
}
